import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Flex } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/components_styles/validation.css";
import "../../assets/styles/components_styles/login.css";
import { isStrongPassword as checkStrongPassword, hashPassword } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import { forgotPasswordUser, selectForgotPasswordSuccess, resetForgotPasswordSuccess } from "../../redux/userSlice";

const { Title, Text } = Typography;

export default function UpdatePassword() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const forgotPasswordSuccess = useSelector(selectForgotPasswordSuccess);

    // Get verified user data from navigation state
    const verifiedType = location.state?.type;
    const verifiedValue = location.state?.value;
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: ""
    });
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef(null);
    const requireCaptcha = !!config.turnstileSiteKey;

    // Redirect to login if no verified data (user came directly without verification)
    useEffect(() => {
        if (!verifiedType || !verifiedValue) {
            navigate("/forgot-password");
        }
    }, [verifiedType, verifiedValue, navigate]);

    // Redirect to login with success message on password update
    useEffect(() => {
        if (forgotPasswordSuccess) {
            dispatch(resetForgotPasswordSuccess());
            navigate("/login", { state: { message: "Password successfully reset!" } });
        }
    }, [forgotPasswordSuccess, navigate, dispatch]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (errors[e.target.name]) {
            setErrors({
                ...errors,
                [e.target.name]: ""
            });
        }
    };

    const handleSubmit = async () => {
        // Validate new password
        if (!formData.newPassword) {
            setErrors({ newPassword: VALIDATION_MESSAGES.PASSWORD_REQUIRED });
            return;
        } else if (!checkStrongPassword(formData.newPassword)) {
            setErrors({ newPassword: VALIDATION_MESSAGES.PASSWORD_WEAK });
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setErrors({ confirmPassword: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH });
            return;
        }
        if (requireCaptcha && !captchaToken) {
            setErrors({ confirmPassword: "Please complete the verification." });
            return;
        }

        setErrors({});

        try {
            const hashedPassword = await hashPassword(formData.newPassword);
            const submissionData = {
                type: verifiedType,
                value: verifiedValue,
                password: hashedPassword
            };
            if (requireCaptcha) submissionData.captchaToken = captchaToken;
            dispatch(forgotPasswordUser(submissionData));
        } catch (error) {
            console.error("Hashing failed", error);
        }
    };

    return (
        <div className="loginPageRoot">
            {/* SEO HELMET WRAPPER */}
            <HelmetWrapper
                title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
                description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                and city-specific visibility for organizers and users." 
                keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
            />

            {/* HEADER */}
            <Header isHome={false} />

            {/* Main content */}
            <main className="loginMain container">
                <div className="loginImage">
                    <img src={loginImg} alt="Update Password" className="sideImage" />
                </div>

                {/* right form */}
                <div className="loginFormWrap">
                    <div className="loginCard">
                        <div className="loginCardInner">
                            <div className="loginCardHeader">
                                <Title level={3} className="title">
                                    Update Password
                                </Title>
                                <Text className="color-neutral">
                                    Enter your new password below.
                                </Text>
                            </div>

                            <Form name="updatePassword" layout="vertical" onFinish={handleSubmit}>
                                <Form.Item
                                    required
                                    label="New Password"
                                    validateStatus={errors.newPassword ? "error" : ""}
                                    help={errors.newPassword}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input.Password
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        prefix={
                                            <span className="material-symbols-outlined prefix-blue">
                                                lock
                                            </span>
                                        }
                                        placeholder="New Password"
                                        size="large"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <Form.Item
                                    required
                                    label="Confirm Password"
                                    validateStatus={errors.confirmPassword ? "error" : ""}
                                    help={errors.confirmPassword}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input.Password
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        prefix={
                                            <span className="material-symbols-outlined prefix-blue">
                                                lock
                                            </span>
                                        }
                                        placeholder="Confirm Password"
                                        size="large"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />

                                <Form.Item style={{ marginTop: 18 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        size="large"
                                        className="btnGradient"
                                    >
                                        Update Password
                                    </Button>
                                </Form.Item>
                                <Flex align="center" justify="center">
                                    <Link to="/login">
                                        <Button type="link">Back to Login</Button>
                                    </Link>
                                </Flex>
                            </Form>
                        </div>
                    </div>
                </div>
            </main>

            {/* FOOTER */}
            <Footer />
        </div>
    );
}
